import { Button } from "components/Button/Button";
import { AutocompleteOption } from "components/Form/components/Autocomplete/Autocomplete";
import { AutocompleteFormComponent } from "components/Form/components/Autocomplete/AutocompleteFormComponent";
import { CancelButton } from "components/Form/components/CancelButton/CancelButton";
import { Form } from "components/Form/components/Form/Form";
import { SwitchFormComponent } from "components/Form/components/Switch/SwitchFormComponent";
import { requiredValidator } from "components/Form/Form.utils";
import { RecordPromptPopover } from "components/Prompt/RecordPromptPopover";
import { create } from "containers/Auth/screens/IdentityManagement/IdentityRole.service";
import { AddButton } from "containers/Resources/screens/ResourcesSingle/components/SubrecordTable";
import { Identity_Role } from "graphql/generated/graphql";
import { useState } from "react";
import { Field, Form as FinalForm } from "react-final-form";
import { useDispatch } from "react-redux";
import { getSearchObj } from "utils/utils";

interface RoleIdentityFormProps {
    closeModal: () => void;
    onSubmit: (formValues: { identityId: string; propagate: boolean }) => void;
    filterOptionList: (option: AutocompleteOption) => boolean;
}

const RoleIdentityForm = ({ closeModal, onSubmit, filterOptionList }: RoleIdentityFormProps) => {
    return (
        <>
            <div className="recordPrompt__header">
                <span>Add identity to the role</span>
            </div>
            <FinalForm onSubmit={onSubmit} initialValues={{ identityId: null, propagate: false }}>
                {({ handleSubmit, pristine, hasValidationErrors }) => {
                    return (
                        <Form onSubmit={handleSubmit}>
                            <Form.Row>
                                <Field
                                    name="identityId"
                                    id="identityId"
                                    component={AutocompleteFormComponent}
                                    fieldAttr={{ required: true }}
                                    fieldProps={{
                                        label: "Identity",
                                        options: {
                                            resource: "identity",
                                            queryField: getSearchObj({
                                                field: "identity_type",
                                                op: "neq",
                                                value: "EXECUTION",
                                            }),
                                            filterOptions: filterOptionList,
                                        },
                                    }}
                                    validate={requiredValidator}
                                />
                            </Form.Row>
                            <Form.Row>
                                <Field
                                    name="propagate"
                                    id="propagate"
                                    fieldProps={{
                                        label: "Propagate",
                                    }}
                                    fieldAttr={{}}
                                    component={SwitchFormComponent}
                                />
                            </Form.Row>
                            <Form.Row>
                                <CancelButton onClick={() => closeModal()} />
                                <Button
                                    submit
                                    variation="primary"
                                    label="Save"
                                    disabled={pristine || hasValidationErrors}
                                />
                            </Form.Row>
                        </Form>
                    );
                }}
            </FinalForm>
        </>
    );
};

export const AddRoleIdentityPopover = ({
    isActionButtonDisabled,
    roleId,
    fetchResourceData,
    currentIdentityList,
}: {
    isActionButtonDisabled: boolean;
    roleId: string;
    fetchResourceData: () => void;
    currentIdentityList: Identity_Role[];
}) => {
    const dispatch = useDispatch();
    const [isOpened, setIsOpened] = useState(false);

    const onSubmit = (formValues) => {
        create(dispatch, formValues.identityId, roleId, formValues.propagate).then(() => {
            setIsOpened(false);
            fetchResourceData();
        });
    };

    const filterOptionList = ({ value }) =>
        !currentIdentityList.find(({ identity_id }) => identity_id === value);

    return (
        <>
            <AddButton
                onClick={() => setIsOpened(true)}
                isDisabled={isActionButtonDisabled}
                tooltip="Add identity to the role"
            />
            <RecordPromptPopover
                paperStyle={{ minWidth: "550px", overflow: "visible" }}
                isOpened={isOpened}
            >
                <RoleIdentityForm
                    onSubmit={onSubmit}
                    closeModal={() => setIsOpened(false)}
                    filterOptionList={filterOptionList}
                />
            </RecordPromptPopover>
        </>
    );
};
